import { clearToken, getToken } from "./auth";

const BASE = "/admin/v1";

// ApiError surfaces the server's error envelope (`{error, code}`) plus the
// HTTP status for the UI to branch on (e.g. 401 → re-prompt for token).
export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set("Authorization", `Bearer ${getToken()}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(BASE + path, { ...init, headers });
  if (res.status === 401) {
    // Só limpa session quando 401 vem de "token rejeitado".
    // 401 com codes específicos (PENDING_EXPIRED, TOO_MANY_ATTEMPTS,
    // etc.) NÃO devem desconectar — são erros de fluxo, não auth.
    let code = "UNAUTHORIZED";
    let message = "token rejected";
    try {
      const body = (await res.clone().json()) as { code?: string; error?: string };
      code = body.code ?? code;
      message = body.error ?? message;
    } catch {
      /* not JSON */
    }
    if (code === "UNAUTHORIZED") {
      clearToken();
      window.dispatchEvent(new Event("admin-token-cleared"));
    }
    throw new ApiError(401, code, message);
  }
  if (!res.ok) {
    let code = "INTERNAL_ERROR";
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { code?: string; error?: string };
      code = body.code ?? code;
      message = body.error ?? message;
    } catch {
      // body not JSON — keep defaults
    }
    throw new ApiError(res.status, code, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// apiBlob baixa um endpoint binário (CSV/PDF/zip etc.) com o mesmo
// envelope de auth do api(). Retorna o Blob; caller decide se faz
// download via <a download> ou processa em memória. Erros seguem o
// mesmo contrato (`{error, code}` JSON) — quando o servidor retorna
// não-OK, lemos o body como JSON e propagamos como ApiError.
export async function apiBlob(
  path: string,
  init: RequestInit = {},
): Promise<Blob> {
  const headers = new Headers(init.headers ?? {});
  headers.set("Authorization", `Bearer ${getToken()}`);
  const res = await fetch(BASE + path, { ...init, headers });
  if (res.status === 401) {
    let code = "UNAUTHORIZED";
    let message = "token rejected";
    try {
      const body = (await res.clone().json()) as { code?: string; error?: string };
      code = body.code ?? code;
      message = body.error ?? message;
    } catch {
      /* not JSON */
    }
    if (code === "UNAUTHORIZED") {
      clearToken();
      window.dispatchEvent(new Event("admin-token-cleared"));
    }
    throw new ApiError(401, code, message);
  }
  if (!res.ok) {
    let code = "INTERNAL_ERROR";
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { code?: string; error?: string };
      code = body.code ?? code;
      message = body.error ?? message;
    } catch {
      /* keep defaults */
    }
    throw new ApiError(res.status, code, message);
  }
  return await res.blob();
}
